// AI-ASSISTED: generated with claude-code on 2026-06-10, reviewed by Yoann Hertienne
import useAsync from 'react-use/lib/useAsync';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CheckIcon from '@mui/icons-material/Check';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { TileBoundary } from '../team-landing/TileBoundary';

const PROJECT_ANNOTATION = 'growthbook.io/project-id';
const STALE_THRESHOLD_MS = 90 * 24 * 60 * 60 * 1000; // 90 days, mirrors luminus-triage-flag-cleanup

export type GrowthbookEnvironmentSetting = {
  enabled?: boolean;
};

export type GrowthbookFeature = {
  id: string;
  project?: string;
  defaultValue?: string | boolean | number;
  dateUpdated?: string;
  archived?: boolean;
  // The GrowthBook v1 features list already embeds per-environment state, so
  // the card reads it here instead of fanning out one GET /features/<id> per
  // flag (T082): no N+1, no concurrency limit, no per-flag cache needed.
  environments?: Record<string, GrowthbookEnvironmentSetting>;
};

export type EnvState = { env: string; enabled: boolean };

// Canonical Luminus environment order (DEV → TST → ACC → PRD). Any environment
// the API returns that isn't in this list is appended alphabetically so a new
// GrowthBook environment still renders (just after the known ones).
export const ENV_ORDER: readonly string[] = [
  'development',
  'test',
  'acceptance',
  'production',
];

/**
 * Flatten a feature's `environments` map into an ordered on/off list. Missing
 * or empty maps yield `[]` (the card then renders no env row for that flag).
 */
export const envStates = (
  feature: Pick<GrowthbookFeature, 'environments'>,
): EnvState[] => {
  const entries = Object.entries(feature.environments ?? {});
  return entries
    .map(([env, setting]) => ({ env, enabled: Boolean(setting?.enabled) }))
    .sort((a, b) => {
      const ai = ENV_ORDER.indexOf(a.env);
      const bi = ENV_ORDER.indexOf(b.env);
      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      return a.env.localeCompare(b.env);
    });
};

export const isStale = (
  feature: Pick<GrowthbookFeature, 'dateUpdated'>,
  now: number = Date.now(),
): boolean => {
  if (!feature.dateUpdated) return false;
  const updated = Date.parse(feature.dateUpdated);
  if (Number.isNaN(updated)) return false;
  return now - updated >= STALE_THRESHOLD_MS;
};

// The proxy already filters by `?project=<id>` upstream. We only re-filter
// client-side to drop archived flags and (defensively) flags whose `project`
// field disagrees with the requested project. Features whose `project`
// field is omitted by the API are kept — relying on the `?project=` filter
// to have done its job.
export const filterByProject = (
  features: ReadonlyArray<GrowthbookFeature>,
  projectId: string,
): GrowthbookFeature[] =>
  features.filter(f => !f.archived && (!f.project || f.project === projectId));

/**
 * Per-environment on/off chips for one flag (T082). State is conveyed by more
 * than colour (WCAG 2.1 AA): enabled chips are filled with a check icon,
 * disabled chips are outlined with no icon, and every chip carries an explicit
 * accessible name. Renders nothing when the feature has no environment data.
 */
export const EnvRolloutRow = ({
  feature,
}: {
  feature: Pick<GrowthbookFeature, 'id' | 'environments'>;
}) => {
  const states = envStates(feature);
  if (states.length === 0) return null;
  return (
    <Stack direction="row" spacing={0.5} sx={{ pt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
      {states.map(({ env, enabled }) => (
        <Chip
          key={env}
          size="small"
          label={env}
          variant={enabled ? 'filled' : 'outlined'}
          color={enabled ? 'success' : 'default'}
          icon={enabled ? <CheckIcon fontSize="small" /> : undefined}
          aria-label={`${feature.id} ${enabled ? 'enabled' : 'disabled'} in ${env}`}
        />
      ))}
    </Stack>
  );
};

const GrowthbookCardBody = () => {
  const { entity } = useEntity();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const projectId = entity.metadata.annotations?.[PROJECT_ANNOTATION];

  const { value, loading, error } = useAsync(async () => {
    if (!projectId) return [] as GrowthbookFeature[];
    const baseUrl = await discoveryApi.getBaseUrl('proxy');
    // GrowthBook v1 features endpoint filters by `?projectId=<id>`
    // (verified 2026-06-10 via curl — the docs sample `?project=` returns
    // unfiltered results).
    const url = `${baseUrl}/growthbook/api/v1/features?projectId=${encodeURIComponent(
      projectId,
    )}&limit=100`;
    const response = await fetchApi.fetch(url);
    if (!response.ok) {
      throw new Error(`GrowthBook request failed: ${response.status}`);
    }
    const payload = (await response.json()) as { features?: GrowthbookFeature[] };
    return filterByProject(payload.features ?? [], projectId);
  }, [projectId]);

  if (!projectId) return null;
  if (error) throw error;

  const features = value ?? [];
  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">GrowthBook</Typography>
          <Chip size="small" label={`project: ${projectId}`} />
        </Stack>
        {loading && (
          <Box sx={{ py: 1 }}>
            <CircularProgress size={20} aria-label="Loading GrowthBook flags" />
          </Box>
        )}
        {!loading && features.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>
            No flags in this GrowthBook project.
          </Typography>
        )}
        {!loading && features.length > 0 && (
          <List dense>
            {features.map(feature => (
              <ListItem
                key={feature.id}
                disableGutters
                sx={{ display: 'block' }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <ListItemText
                    primary={feature.id}
                    secondary={`default: ${String(feature.defaultValue ?? 'unset')}`}
                  />
                  {isStale(feature) && (
                    <Chip
                      size="small"
                      color="warning"
                      label="stale (≥ 3 months)"
                      aria-label={`${feature.id} is stale`}
                    />
                  )}
                </Stack>
                <EnvRolloutRow feature={feature} />
              </ListItem>
            ))}
          </List>
        )}
      </CardContent>
      <CardActions>
        <Button
          variant="outlined"
          size="small"
          href={`https://app.growthbook.io/features?project=${encodeURIComponent(projectId)}`}
          target="_blank"
          rel="noreferrer"
          aria-label="Open GrowthBook project for this component"
        >
          Open in GrowthBook
        </Button>
      </CardActions>
    </Card>
  );
};

export const GrowthbookCard = () => (
  <TileBoundary tool="GrowthBook">
    <GrowthbookCardBody />
  </TileBoundary>
);
